import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../utils/supabaseClient';

const MediaPreview = () => {
  const [expandedMedia, setExpandedMedia] = useState(null);

  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchLatestMedia() {
      setLoading(true);
      try {
        // ใช้ select('*') เหมือนใน admin page
        const { data, error } = await supabase
          .from('media_library')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(6);
        
        if (error) {
          console.error('Error fetching media:', error);
          console.error('Error details:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          // ไม่ throw error แต่ให้ set เป็น array ว่างแทน
          if (isMounted) {
            setMediaItems([]);
            setLoading(false);
          }
          return;
        }
        console.log('Fetched media data:', data);
        console.log('Media data length:', data?.length);
        const mapped = (data || []).map((m) => {
          // แปลง media_urls จาก JSON string เป็น array
          let mediaUrls = [];
          if (m.media_urls) {
            try {
              mediaUrls = typeof m.media_urls === 'string' 
                ? JSON.parse(m.media_urls) 
                : (Array.isArray(m.media_urls) ? m.media_urls : []);
            } catch (e) {
              mediaUrls = [];
            }
          }
          
          return {
            id: m.id,
            title: m.title || 'ไม่มีชื่อ',
            summary: (m.description || '').slice(0, 160),
            content: m.description || '',
            imageUrl: m.image_url || '',
            mediaUrls: mediaUrls,
            date: m.date || m.created_at, // ใช้ date ถ้ามี ไม่มีใช้ created_at
            category: 'สื่อประชาสัมพันธ์',
            priority: 'ปกติ'
          };
        });
        console.log('Mapped media items:', mapped);
        console.log('Mapped items length:', mapped?.length);
        if (isMounted) {
          setMediaItems(mapped);
          console.log('Set mediaItems state:', mapped);
        }
      } catch (e) {
        console.error('Error in fetchLatestMedia:', e);
        if (isMounted) setMediaItems([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchLatestMedia();
    return () => {
      isMounted = false;
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'สูง':
        return 'bg-error text-error-foreground';
      case 'ปกติ':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const toggleExpand = (mediaId) => {
    setExpandedMedia(expandedMedia === mediaId ? null : mediaId);
  };

  // Debug: log current state
  console.log('MediaPreview render - loading:', loading, 'mediaItems:', mediaItems, 'length:', mediaItems?.length);

  // ซ่อนถ้าไม่มีข้อมูล (เหมือนเดิม)
  if (loading) {
    return null;
  }

  if (mediaItems.length === 0) {
    return null;
  }

  return (
    <section className="py-12 lg:py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
            สื่อประชาสัมพันธ์
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            รูปภาพและสื่อประชาสัมพันธ์จากที่ว่าการอำเภอเทิง
          </p>
        </div>

        {/* Media horizontal marquee using existing Tailwind animate-marquee effect */}
        <div className="relative flex w-full flex-col items-center justify-center overflow-hidden mb-8">
            <div className="group flex overflow-hidden p-2 [--gap:1rem] [gap:var(--gap)] flex-row [--duration:40s]">
              <div className="flex shrink-0 justify-around [gap:var(--gap)] animate-marquee flex-row group-hover:[animation-play-state:paused]">
                {[...Array(2)].map((_, setIndex) => (
                  mediaItems.map((media, idx) => {
                  // หารูปภาพแรกที่จะแสดง (จาก media_urls หรือ image_url)
                  let displayImage = null;
                  if (media?.mediaUrls && media.mediaUrls.length > 0) {
                    const firstImage = media.mediaUrls.find(m => m.type === 'image');
                    if (firstImage) {
                      displayImage = firstImage.url;
                    }
                  }
                  if (!displayImage && media?.imageUrl) {
                    displayImage = media.imageUrl;
                  }
                  
                  return (
                    <div
                      key={`${setIndex}-${media?.id}-${idx}`}
                      className="bg-card rounded-lg shadow-government hover:shadow-government-lg transition-government overflow-hidden w-80 flex-shrink-0 flex flex-col"
                    >
                      {/* แสดงรูปภาพด้านบน */}
                      {displayImage && (
                        <div className="w-full h-48 overflow-hidden bg-muted">
                          <img
                            src={displayImage}
                            alt={media?.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-start justify-between mb-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(media?.priority)}`}>
                            {media?.priority}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(media?.date)}
                          </span>
                        </div>

                        <h3 className="text-lg font-semibold text-foreground mb-3 leading-tight line-clamp-2">
                          {media?.title}
                        </h3>

                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-3 flex-1">
                          {expandedMedia === media?.id ? media?.content : media?.summary}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
                          <div className="flex items-center space-x-2">
                            <Icon name="Tag" size={14} className="text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{media?.category}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="ChevronRight"
                            iconPosition="right"
                            asChild
                          >
                            <Link to={`/media/${media?.id}`}>
                              ดูรายละเอียด
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                  })
                ))}
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-1/3 bg-gradient-to-r from-muted/30 sm:block" />
            <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/3 bg-gradient-to-l from-muted/30 sm:block" />
          </div>

        <div className="text-center">
          <Button
            variant="outline"
            iconName="Image"
            iconPosition="left"
            asChild
          >
            <Link to="/news-and-announcements/media">
              ดูสื่อทั้งหมด
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default MediaPreview;

